/**
 * @author: lv.chen
 * @date: 2025/2/20
 * @desc: Pipeline / Change Log页面 Change Detail页签 筛选器
 */
import type { IDynamicFilterControl } from '@gp/components/DynamicFilter/components/FilterControl/types';
import type { IDynamicFilterField } from '@gp/components/DynamicFilter/types';
import FilterCommon from '@gp/components/FilterCommon';
import { COMMON_DATE_TYPES } from '@gp/components/RangePicker';
import { KEYS_MAPPING, NAMES_MAPPING } from '@gp/constants/field';
import { COMMON_SELECT_MULTIPLE_CONTROL_CFG } from '@gp/constants/select';
import type {
  IPipelineChangeDetailFilterConditionsVO,
  IPipelineChangeDetailFilterVO,
} from '@gp/types/changeLog';
import type { IRecord } from '@gp/types/global';
import { getDisabledDateByDateType } from '@gp/utils/date';
import type { IBaseProps } from '@home/types/common';
import { cloneDeep, isEqual, merge } from 'lodash-es';
import React, { useEffect, useMemo, useState } from 'react';

interface IChangeDetailFilterProps extends IBaseProps {
  loading?: boolean;
  data?: IPipelineChangeDetailFilterConditionsVO | null;
  value?: IPipelineChangeDetailFilterVO;
  onChange?: (payload: IPipelineChangeDetailFilterVO) => void;
}

const DEFAULT_CONTROLS: IRecord<IDynamicFilterControl> = {
  rangePicker: {
    showSelectType: true,
    showShortcut: true,
    typeList: COMMON_DATE_TYPES,
  },
  pteam: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    listWidth: 260,
    options: [],
  },
  studio: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    matchButtonWidth: false,
    listWidth: 260,
    options: [],
  },
  tab: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    searchable: false,
    options: [],
  },
  update_user: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    options: [],
  },
  stage: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    searchable: false,
    options: [],
  },
};

const ChangeDetailFilter: React.FC<IChangeDetailFilterProps> = (props) => {
  const {
    style,
    className,
    loading = true,
    data,
    value: valueProps,
    onChange: onChangeProps,
    allFilterOptionsValues,
    onReset,
    scrollBody,
    headerTitle,
    currentPage,
    updateDeps,
    handleSearch,
    getSearchList,
    formatSearchResults,
    searchVal,
    searchList,
    showRightBtn,
    selectedValue,
    setSelectedValue,
    setSearchList,
    setSearchVal,
  } = props;

  const controls = useMemo<IRecord<IDynamicFilterControl>>(() => {
    if (loading) return DEFAULT_CONTROLS;
    const {
      rangePicker: rangePickerOptions,
      pteam: pteamOptions,
      studio: studioMappings,
      tab: tabOptions,
      update_user: updateUserOptions,
      stage: stageOptions,
      project_id: cascadeProjectOptions,
    } = data || {};
    const { minDate, maxDate } = rangePickerOptions || {};
    const controlsTmp = cloneDeep(DEFAULT_CONTROLS);

    return merge(controlsTmp, {
      rangePicker: {
        startOfTime: minDate,
        endOfTime: maxDate,
        disabledDate: {
          date: getDisabledDateByDateType('date', minDate, maxDate),
          week: getDisabledDateByDateType('week', minDate, maxDate),
          month: getDisabledDateByDateType('month', minDate, maxDate),
        },
        disabledMonth: getDisabledDateByDateType('month', minDate, maxDate),
      },
      // pteam: {
      //   options: pteamOptions,
      // },
      // studio: {
      //   options: studioMappings,
      // },
      project_id: {
        options: cascadeProjectOptions,
        allValues: allFilterOptionsValues?.project_id || [],
        btnTitle: 'P-team/BO/Studio/Project',
      },
      tab: {
        options: tabOptions,
      },
      update_user: {
        options: updateUserOptions,
      },
      stage: {
        options: stageOptions,
      },
    });
  }, [loading, data]);

  const [value, setValue] = useState<IPipelineChangeDetailFilterVO>({
    rangePicker: {
      dateType: '',
      startDate: '',
      endDate: '',
    },
    // pteam: [],
    // studio: [],
    project_id: [],
    tab: [],
    update_user: [],
    stage: [],
  });

  const fields = useMemo<IDynamicFilterField[]>(() => {
    const { project_id: projectOptions } = data || {};
    const projectDisplay = loading
      ? false
      : Array.isArray(projectOptions) && projectOptions.length > 0;
    return [
      {
        key: 'rangePicker',
        type: 'rangePicker',
        label: '',
        saveKey: 'changeLog_rangePicker',
      },
      {
        key: KEYS_MAPPING.CASCADE_PROJECT,
        type: 'cascadeProject',
        display: projectDisplay,
        label: NAMES_MAPPING.CASCADE_PROJECT,
      },
      { key: 'tab', type: 'selectMultiple', label: 'Tab' },
      { key: 'update_user', type: 'selectMultiple', label: 'Last Update' },
      { key: 'stage', type: 'selectMultiple', label: 'Stage' },
    ];
  }, [data]);

  const handleChange = (payload: IRecord<any>) => {
    const valueTmp = { ...value, ...payload };
    setValue(valueTmp);
  };

  const handleConfirm = () => {
    onChangeProps?.(value);
  };

  useEffect(() => {
    if (!isEqual(value, valueProps)) {
      setValue({ ...value, ...valueProps });
    }
  }, [valueProps]);

  return (
    <FilterCommon
      style={style}
      className={className}
      controls={controls}
      value={value}
      onChange={handleChange}
      isNeedDiff={true}
      fields={fields}
      confirm={handleConfirm}
      onReset={onReset}
      loading={loading}
      filterSearchHeight={130}
      filterHeight={66}
      scrollBody={scrollBody}
      headerTitle={headerTitle}
      showRightBtn={showRightBtn}
      currentPage={currentPage}
      formatSearchResults={formatSearchResults}
      updateDeps={updateDeps}
      searchVal={searchVal}
      searchList={searchList}
      handleSearch={handleSearch}
      getSearchList={getSearchList}
      selectedValue={selectedValue}
      setSelectedValue={setSelectedValue}
      setSearchList={setSearchList}
      setSearchVal={setSearchVal}
    />
  );
};

export default ChangeDetailFilter;
